import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Stepper from "../../../components/misc/Stepper";
import { SEO } from "../../../components/seo";
import Button from "../../../components/ui/Button";
import Text from "../../../components/ui/Text";
import { PAGE_SEO } from "../../../constants/seo";
import {
  isDateTimeValid,
  isGroupStepValid,
  isLocationValid,
  isPaymentValid,
  isPlayerDetailsValid,
  isSportVisibilityValid,
} from "../shared/gameFormValidation";
import { GroupStep, ReviewStep } from "./parts";
import {
  DateTimeSelection,
  GameTypeSelection,
  LocationSelection,
  PaymentCollection,
  PlayerDetails,
} from "./parts/GameStep/parts";

const WIZARD_STEPS = [
  { value: "sport", label: "Sport" },
  { value: "datetime", label: "Date & time" },
  { value: "location", label: "Location" },
  { value: "players", label: "Players" },
  { value: "payment", label: "Payment" },
  { value: "group", label: "Group" },
  { value: "review", label: "Review" },
];

const CreateGameWizard = ({
  gameData,
  setGameData,
  groupData,
  selectedGroup,
  setSelectedGroup,
  showCreateGroup,
  setShowCreateGroup,
  existingGroups,
  fetchingGroups,
  isGroupCreating,
  isLoading,
  handleGroupInputChange,
  handleImageChange,
  handleGameInputChange,
  handleGameTypeChange,
  handleSportChange,
  handleLocationSelect,
  handleAddressChange,
  handleVenueChange,
  handlePlayerAmountChange,
  handleRevolutToggle,
  handleRevolutLinkChange,
  handleSubmitGame,
  createGroupAPI,
  handleSelectGroup,
  handleCreateNewGroup,
  isGroupSelected,
}) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(1);
  const hasAutoSelectedGroup = useRef(false);

  useEffect(() => {
    if (
      activeStep === 6 &&
      !hasAutoSelectedGroup.current &&
      existingGroups.length > 0 &&
      !selectedGroup &&
      !showCreateGroup
    ) {
      hasAutoSelectedGroup.current = true;
      setSelectedGroup(existingGroups[0]);
    }
  }, [
    activeStep,
    existingGroups,
    selectedGroup,
    showCreateGroup,
    setSelectedGroup,
  ]);

  const STEP_VALIDATORS = {
    1: isSportVisibilityValid(gameData),
    2: isDateTimeValid(gameData),
    3: isLocationValid(gameData),
    4: isPlayerDetailsValid(gameData),
    5: isPaymentValid(gameData),
    6: isGroupStepValid({ selectedGroup, showCreateGroup, groupData }),
  };

  const isCurrentStepValid = STEP_VALIDATORS[activeStep] ?? true;

  const handleBack = () => {
    setActiveStep((step) => Math.max(1, step - 1));
  };

  const handleNext = () => {
    if (!isCurrentStepValid) return;
    setActiveStep((step) => Math.min(WIZARD_STEPS.length, step + 1));
  };

  const handlePublish = async (e) => {
    let groupId = selectedGroup?.id;
    if (showCreateGroup) {
      const newGroup = await createGroupAPI();
      if (!newGroup) return;
      groupId = newGroup.id;
    }

    const result = await handleSubmitGame(e, groupId);
    if (result?.success) {
      navigate("/game-success", {
        state: {
          gameDetails: result.data,
          gameCreated: true,
        },
      });
    }
  };

  const renderStep = () => {
    switch (activeStep) {
      case 1:
        return (
          <GameTypeSelection
            gameData={gameData}
            onGameTypeChange={handleGameTypeChange}
            onSportChange={handleSportChange}
          />
        );
      case 2:
        return (
          <DateTimeSelection
            gameData={gameData}
            onGameInputChange={handleGameInputChange}
            onGameDataChange={setGameData}
          />
        );
      case 3:
        return (
          <LocationSelection
            gameData={gameData}
            onLocationSelect={handleLocationSelect}
            onAddressChange={handleAddressChange}
            onVenueChange={handleVenueChange}
            onHowToFindUsChange={handleGameInputChange("how_to_find")}
          />
        );
      case 4:
        return (
          <PlayerDetails
            gameData={gameData}
            onGameInputChange={handleGameInputChange}
          />
        );
      case 5:
        return (
          <PaymentCollection
            gameData={gameData}
            onPlayerAmountChange={handlePlayerAmountChange}
            onRevolutToggle={handleRevolutToggle}
            onRevolutLinkChange={handleRevolutLinkChange}
            onGameDataChange={setGameData}
          />
        );
      case 6:
        return (
          <GroupStep
            fetchingGroups={fetchingGroups}
            existingGroups={existingGroups}
            showCreateGroup={showCreateGroup}
            groupData={groupData}
            onGroupInputChange={handleGroupInputChange}
            onImageChange={handleImageChange}
            onSelectGroup={handleSelectGroup}
            onCreateNewGroup={handleCreateNewGroup}
            onUseExistingGroup={() => setShowCreateGroup(false)}
            isGroupSelected={isGroupSelected}
          />
        );
      case 7:
        return (
          <ReviewStep
            gameData={gameData}
            group={showCreateGroup ? groupData : selectedGroup}
            isNewGroup={showCreateGroup}
            onEditStep={setActiveStep}
          />
        );
      default:
        return null;
    }
  };

  const isLastStep = activeStep === WIZARD_STEPS.length;
  const isPublishing = isLoading || isGroupCreating;

  return (
    <Box>
      <Text variant="pageHeader" sx={{ mb: 3 }}>
        Create a game
      </Text>

      <SEO
        title={PAGE_SEO.CREATE_GAME.title}
        description={PAGE_SEO.CREATE_GAME.description}
        keywords={PAGE_SEO.CREATE_GAME.keywords}
      />

      <Stepper steps={WIZARD_STEPS} activeStep={activeStep} />

      <Box
        sx={{
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: 2 },
          p: { xs: 0, md: 3 },
          mt: { xs: 3 },
          mb: { xs: 4, md: 4 },
          border: { xs: "none", md: "1px solid #E5E5E5" },
          maxWidth: "600px",
        }}
      >
        {renderStep()}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mt: 3,
          pt: 3,
          borderTop: { xs: "none", md: "1px solid #E5E5E5" },
        }}
      >
        {activeStep > 1 && (
          <Button
            variant="secondary"
            onClick={handleBack}
            sx={{
              py: 1.5,
              px: 3,
              width: "auto",
              fontSize: "16px",
              fontWeight: 500,
              borderRadius: "8px",
              border: "1px solid #E5E5E5",
              backgroundColor: "white",
              color: "#333",
              "&:hover": {
                borderColor: "#1990FF",
                backgroundColor: "#F8F9FA",
              },
            }}
          >
            Back
          </Button>
        )}
        <Button
          variant="primary"
          type="button"
          onClick={isLastStep ? handlePublish : handleNext}
          disabled={!isCurrentStepValid || isPublishing}
          sx={{
            py: 1.5,
            px: 3,
            width: "auto",
            fontSize: "16px",
            fontWeight: 500,
            borderRadius: "8px",
            backgroundColor: "#1990FF",
            color: "white",
            "&:hover": {
              backgroundColor: "#1677FF",
            },
            "&.Mui-disabled": {
              color: "#A1A1A1 !important",
              backgroundColor: "#F5F5F5 !important",
              border: "1px solid #D4D4D4",
            },
          }}
        >
          {isLastStep
            ? isPublishing
              ? "Publishing game..."
              : "Publish game"
            : "Next"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateGameWizard;
